/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import MainNavigation from "@/components/layout/main-navigation";
import { EMAILS } from "@/lib/constants/master";
import { Check, Minus } from "lucide-react";
import { FadeIn } from "@/components/landing/primitives/fade-in";
import type { CompareConfig } from "./types";

function FeatureValue({ value }: { value: string | boolean }) {
  if (value === true) return <Check className="h-4 w-4 text-success" />;
  if (value === false)
    return <Minus className="h-4 w-4 text-muted-foreground/40" />;
  return <span className="text-sm text-foreground">{value}</span>;
}

export function CompareTemplate({ config }: { config: CompareConfig }) {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      {/* Hero */}
      <MarketingPageHero
        variant="dark"
        badge={config.hero.badge}
        title={
          <>
            {config.hero.title}{" "}
            <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
              {config.hero.titleHighlight}
            </span>
          </>
        }
        description={config.hero.description}
      />

      {/* They're Great At */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <header className="mb-8 md:mb-12 max-w-3xl">
            <span className="section-badge mb-4">
              What {config.competitorName} Does Well
            </span>
            <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold text-foreground mb-3 font-display">
              {config.theyreGreatAt.title}
            </h2>
            <p className="text-[clamp(14px,1.5vw,16px)] leading-[1.5] text-muted-foreground">
              {config.theyreGreatAt.description}
            </p>
          </header>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {config.theyreGreatAt.items.map((item, i) => (
              <FadeIn
                key={item.title}
                delay={i * 100}
                className="rounded-2xl border border-border bg-muted/30 p-6"
              >
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </FadeIn>
            ))}
          </div>

          <div className="rounded-xl bg-muted/20 border border-border p-6">
            <p className="text-sm font-medium text-foreground mb-3">
              {config.competitorName} is a strong choice if you are:
            </p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {config.bestFor.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm text-muted-foreground"
                >
                  <Check className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Where CC Fits */}
      <section className="py-16 md:py-24 bg-background-warm">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <header className="mb-8 md:mb-12 max-w-3xl">
            <span className="section-badge mb-4">
              Where Contractor Co-Pilot Fits
            </span>
            <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold text-foreground mb-3 font-display">
              {config.weBuiltFor.title}
            </h2>
            <p className="text-[clamp(14px,1.5vw,16px)] leading-[1.5] text-muted-foreground">
              {config.weBuiltFor.description}
            </p>
          </header>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {config.weBuiltFor.items.map((item, i) => (
              <FadeIn
                key={item.title}
                delay={i * 100}
                className="rounded-2xl border border-secondary/20 bg-secondary/5 p-6"
              >
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </FadeIn>
            ))}
          </div>

          <div className="rounded-xl bg-secondary/5 border border-secondary/20 p-6">
            <p className="text-sm font-medium text-foreground mb-3">
              Contractor Co-Pilot is built for contractors who:
            </p>
            <ul className="grid sm:grid-cols-2 gap-2">
              {config.ccBestFor.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm text-muted-foreground"
                >
                  <Check className="h-4 w-4 text-secondary mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <header className="mb-8 md:mb-12 max-w-3xl">
            <span className="section-badge mb-4">Feature Comparison</span>
            <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold text-foreground mb-3 font-display">
              Side by side
            </h2>
          </header>

          <div className="rounded-2xl border border-border overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[1fr_120px_120px] md:grid-cols-[1fr_160px_160px] bg-muted/40 border-b border-border">
              <div className="px-4 py-3 text-sm font-medium text-muted-foreground">
                Feature
              </div>
              <div className="px-4 py-3 text-sm font-medium text-muted-foreground text-center">
                {config.competitorName}
              </div>
              <div className="px-4 py-3 text-sm font-medium text-secondary text-center">
                CC
              </div>
            </div>

            {config.comparison.categories.map((category) => (
              <div key={category.name}>
                {/* Category header */}
                <div className="px-4 py-2 bg-muted/20 border-b border-border">
                  <span className="text-xs font-bold text-foreground uppercase tracking-wider">
                    {category.name}
                  </span>
                </div>
                {/* Feature rows */}
                {category.features.map((feature, i) => (
                  <div
                    key={feature.name}
                    className={`grid grid-cols-[1fr_120px_120px] md:grid-cols-[1fr_160px_160px] border-b border-border/50 ${
                      i % 2 === 1 ? "bg-muted/10" : ""
                    }`}
                  >
                    <div className="px-4 py-3 text-sm text-foreground">
                      {feature.name}
                    </div>
                    <div className="px-4 py-3 flex items-center justify-center">
                      <FeatureValue value={feature.competitor} />
                    </div>
                    <div className="px-4 py-3 flex items-center justify-center">
                      <FeatureValue value={feature.cc} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="py-16 md:py-24 bg-background-warm">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <header className="mb-8 md:mb-12 text-center">
            <span className="section-badge mb-4">Pricing</span>
            <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold text-foreground mb-3 font-display">
              What you pay
            </h2>
          </header>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="rounded-2xl border border-border bg-background p-6">
              <h3 className="text-lg font-semibold text-foreground mb-1">
                {config.competitorName}
              </h3>
              <p className="text-2xl font-bold text-foreground mb-2">
                {config.pricing.competitorRange}
              </p>
              <p className="text-sm text-muted-foreground">
                {config.pricing.competitorNote}
              </p>
            </div>
            <div className="rounded-2xl border-2 border-secondary bg-secondary/5 p-6">
              <h3 className="text-lg font-semibold text-foreground mb-1">
                Contractor Co-Pilot
              </h3>
              <p className="text-2xl font-bold text-secondary mb-2">
                {config.pricing.ccRange}
              </p>
              <p className="text-sm text-muted-foreground">
                {config.pricing.ccNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {config.faq.length > 0 && (
        <section className="py-16 md:py-24 bg-background">
          <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
            <header className="mb-8 md:mb-12 max-w-3xl">
              <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.1] font-bold text-foreground mb-3 font-display">
                Common questions
              </h2>
            </header>
            <div className="max-w-3xl space-y-6">
              {config.faq.map((item) => (
                <div key={item.question}>
                  <h3 className="text-base font-semibold text-foreground mb-2">
                    {item.question}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <MarketingCTASection
        title={config.cta.title}
        description={config.cta.description}
        primaryCTA={{
          text: "Start Your Free Trial",
          href: "/auth/sign-up",
        }}
        secondaryCTA={{
          text: "Contact Us",
          href: `mailto:${EMAILS.CONTACT}`,
        }}
        trustIndicators={[
          "7-day free trial",
          "No long-term contracts",
          "Cancel anytime",
        ]}
      />

      <Footer />
    </div>
  );
}
